import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Slider } from '@mui/material';
import PauseIcon from '@mui/icons-material/PauseCircleOutlined';
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import PlaybackSpeed from '@mui/icons-material/ScheduleOutlined';
import FullScreen from '@mui/icons-material/ZoomOutMapOutlined';
import Stack from '@mui/material/Stack';
import cx from 'classnames';
import MuteIcon from '@mui/icons-material/VolumeMute';

import PlayIcon from '../../../../assets/icons/play-icon-timeline.svg';
import ExitFullScreen from '../../../../assets/icons/exit-full-screen.svg';
import VolumeIcon from '../../../../assets/icons/volume-icon.svg';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  setComposerMarkerPosition,
  triggerRefresh,
  updateComposerPlayControls,
  updateCurrentSelectedComposerClips,
  updateCustomTime,
  updateProgressPosition,
  updateSelectedMultipleClips,
} from '../../../../store/composers/composersSlice';
import { timeLineFormatTime } from '../../../../helpers/timelineFormatTime';
import { COMPOSER_CLIP_RIGHT_MARGIN } from '../../../Composer/ComposerRow';
import {
  checkPositiveNumber,
  findSearchValue,
  rememberManualVolumeOverrideForFeed,
} from '../../../../helpers';
import NotesForVideo from '../../NotesForVideo';
import { toFollowPlayhead, updateSecondaySelectedClips, updateShowNotes } from '../../../../store/sharedStore/sharedStoreSlice';
import { TClip } from '../../../Timeline/TimelineClip';
import styles from './ComposerVideoControls.module.scss';
import VideoQualitySelector from '../../VideoQualitySelector';
import qualityMenuStyles from '../../VideoQualitySelector/VideoQualitySelector.module.scss';
import { VIDEO_TYPE } from '../../../../enums';
import useOnClickOutside from '../../../../helpers/useOnClickOutside';

type TComposerVideoControls = {
  fullScreenHandler: () => void;
  activeFullScreen: boolean;
  videoData?: VIDEO_TYPE;
};

const ComposerVideoControls: React.FC<TComposerVideoControls> = ({ fullScreenHandler, activeFullScreen, videoData }) => {
  const [speedAnchor, setAnchorForSpeedOption] = React.useState<null | HTMLElement>(null);
  const { composerPlayControl, composerRows, customTime } = useAppSelector((state) => state.composersStore);
  const { showNotes } = useAppSelector((state) => state.sharedStore);
  const dispatch = useAppDispatch();

  const switchPlaybackOptionsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorForSpeedOption(speedAnchor ? null : event.currentTarget);
  };

  const speedOptionMenuRef = useRef<HTMLDivElement | null>(null);

  const [showFullScreenSpeedOption, setShowFullScreenSpeedOption] = useState(false);
  const showFullScreenSpeedOpitonMenu = () => {
    setShowFullScreenSpeedOption(true);
  };

  const closeFullScreenSpeedOptionMenu = () => {
    setShowFullScreenSpeedOption(false);
  };

  useOnClickOutside(speedOptionMenuRef, (e) => {
    e.stopPropagation();
    setAnchorForSpeedOption(null);
    closeFullScreenSpeedOptionMenu();
  });

  const selectSpeed = (e: React.MouseEvent<HTMLDivElement>) => {
    alert(e.currentTarget.id);
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playbackRate: Number(e.currentTarget.id),
      }),
    );
    setAnchorForSpeedOption(null);
  };

  const [showVolume, setShowVolume] = useState(false);
  const feedId = findSearchValue('feedId');
  const handleVolumeChange = (event: Event, newValue: number | number[]) => {
    const nextVolume = (newValue as number) / 100;
    rememberManualVolumeOverrideForFeed(feedId, nextVolume);
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        volume: nextVolume,
      }),
    );
  };

  const showVolumeSlider = () => {
    setShowVolume(true);
  };

  const closeVolumeSlider = () => {
    setShowVolume(false);
  };

  const switchFullScreen = () => {
    fullScreenHandler();
    setAnchorForSpeedOption(null);
    setShowFullScreenSpeedOption(false);
  };

  const onPlayPause = () => {
    dispatch(toFollowPlayhead(!composerPlayControl.composerControls.playing));
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playing: !composerPlayControl.composerControls.playing,
      }),
    );
  };

  const videoLength = useMemo(() => composerPlayControl.composerVideoLength, [composerPlayControl.composerVideoLength]);
  const [rowsPixels, setRowsPixels] = useState<number[]>([]);
  const videoWidth = useMemo(() => {
    if (composerRows.length === 0) return 0;
    const pixelArr: number[] = [];
    const allClips = composerRows.flatMap((row, rowIndex) => {
      pixelArr.push(row.clips.reduce((acc, clip) => acc + clip.width + COMPOSER_CLIP_RIGHT_MARGIN, 0));

      return row.clips.map((clip) => ({
        ...clip,
        rowIndex,
      }));
    });
    setRowsPixels(pixelArr);
    return (
      allClips.reduce((acc, clip) => acc + clip.width + COMPOSER_CLIP_RIGHT_MARGIN, 0) -
      (composerRows.length + 1 * COMPOSER_CLIP_RIGHT_MARGIN)
    );
  }, [composerRows]);

  const muteSwitch = () => {
    const nextVolume = composerPlayControl.composerControls.volume > 0 ? 0 : 1;
    rememberManualVolumeOverrideForFeed(feedId, nextVolume);
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        volume: nextVolume,
      }),
    );
  };

  const switchShowNotes = () => {
    dispatch(updateShowNotes(!showNotes));
  };

  const onPositionChange = (_: any, value: number | number[]) => {
    const positionInSeconds = (Number(value) / videoWidth) * videoLength;

    let accumulatedTime = 0;
    let targetClip: TClip | null = null;
    let clipExactTime = 0;

    for (const composerClip of composerRows) {
      for (const clip of composerClip.clips) {
        const clipDuration = clip.time_end - clip.time_start;
        accumulatedTime += clipDuration;

        if (accumulatedTime >= positionInSeconds) {
          targetClip = clip;
          clipExactTime = clip.time_start + (positionInSeconds - (accumulatedTime - clipDuration));
          break;
        }
      }
      if (targetClip) break;
    }

    if (!targetClip) return;

    const rowIndex = targetClip.rowIndex!;
    const clipIndex = targetClip.clipIndex!;
    const t = Math.min(Math.max(clipExactTime, targetClip.time_start), targetClip.time_end);

    // move marker + time
    dispatch(setComposerMarkerPosition({ rowIndex, clipIndex }));
    dispatch(updateCustomTime(t));
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playedSeconds: t,
      }),
    );

    // NEW: ensure the clip under the playhead is selected
    dispatch(updateSelectedMultipleClips(false));
    dispatch(updateSecondaySelectedClips([]));
    dispatch(
      updateCurrentSelectedComposerClips([
        {
          ...targetClip,
          rowIndex,
          clipIndex,
          topicId: composerRows[rowIndex].id,
          topicName: composerRows[rowIndex].name,
        },
      ]),
    );

    dispatch(triggerRefresh());
  };

  const progressSeconds = useMemo(() => {
    const r = composerPlayControl.currentRowIndex;
    const c = composerPlayControl.currentClipIndex;
    const now = composerPlayControl.composerControls.playedSeconds || customTime || 0;

    if (!composerRows.length || r == null || c == null) return 0;

    // sum durations BEFORE current row/clip
    let acc = 0;
    for (let rr = 0; rr < r; rr++) {
      for (const cl of composerRows[rr]?.clips || []) acc += cl.time_end - cl.time_start;
    }
    for (let i = 0; i < c; i++) {
      const cl = composerRows[r]?.clips?.[i];
      if (cl) acc += cl.time_end - cl.time_start;
    }

    // add in-clip offset
    const active = composerRows[r]?.clips?.[c];
    if (!active) return acc;
    const dur = active.time_end - active.time_start;
    const inClip = Math.max(0, Math.min(now - active.time_start, dur));
    return acc + inClip;
  }, [
    composerRows,
    composerPlayControl.currentRowIndex,
    composerPlayControl.currentClipIndex,
    composerPlayControl.composerControls.playedSeconds,
    customTime,
  ]);

  const progressValue = useMemo(() => {
    if (!videoWidth || !videoLength) return 0;
    return (progressSeconds / videoLength) * videoWidth;
  }, [progressSeconds, videoWidth, videoLength]);

  // elapsed in concatenated timeline (from earlier step)
  const elapsed = Math.min(Math.max(progressSeconds, 0), Math.max(videoLength || 0, 0));

  // already have: progressSeconds (concatenated) and dispatch, updateProgressPosition imported

  useEffect(() => {
    dispatch(updateProgressPosition(progressSeconds));
  }, [progressSeconds]);

  return (
    <div className={styles.controls}>
      {!activeFullScreen ? (
        <div className={styles.rangeWrapper}>
          <div className={styles.secondsPlayed}>
            {composerPlayControl.composerControls.playing ? (
              <PauseIcon onClick={onPlayPause} className={styles.pauseIcon} />
            ) : (
              <img onClick={onPlayPause} className={styles.playIcon} src={PlayIcon} alt="play video" />
            )}
            <span>
              {timeLineFormatTime(elapsed)} / {timeLineFormatTime(videoLength || 0)}
            </span>
          </div>
          <Slider
            min={0}
            max={videoWidth === videoWidth ? videoWidth : 100}
            value={progressValue}
            sx={{
              '& .MuiSlider-thumb': {
                display: 'none',
              },
              '& .MuiSlider-rail': {
                color: 'black',
                height: '7px',
              },
              '& .MuiSlider-track': {
                color: 'white',
                height: '8px',
              },
            }}
            onChange={onPositionChange}
          />
        </div>
      ) : (
        // full screen controls
        <div className={styles.fullScreen}>
          <NotesForVideo
            className={showNotes ? styles.notes : styles.hiddenNotes}
            switchShowNotes={switchShowNotes}
            showNotes={showNotes}
          />
          <div className={styles.fullScreenControls}>
            <div className={styles.leftSide}>
              <div className={styles.secondsPlayed}>
                {composerPlayControl.composerControls.playing ? (
                  <PauseIcon onClick={onPlayPause} className={styles.pauseIcon} />
                ) : (
                  <img onClick={onPlayPause} className={styles.playIcon} src={PlayIcon} alt="play video" />
                )}
                <span>
                  {timeLineFormatTime(elapsed)} / {timeLineFormatTime(videoLength || 0)}
                </span>
              </div>
            </div>

            <div className={styles.rightSide}>
              <div className={styles.volumeContainer} onMouseEnter={showVolumeSlider} onMouseLeave={closeVolumeSlider}>
                {showVolume && (
                  <div className={styles.volumeSlider}>
                    <Box sx={{ width: 200, marginTop: '-3px' }}>
                      <Stack spacing={2} sx={{ mb: 1 }} alignItems="center">
                        <Slider
                          color="secondary"
                          aria-label="Volume"
                          value={composerPlayControl.composerControls.volume * 100}
                          onChange={handleVolumeChange}
                          sx={{
                            '& .MuiSlider-thumb': {
                              display: 'none',
                            },
                          }}
                        />
                      </Stack>
                    </Box>
                  </div>
                )}
                <div onClick={muteSwitch} className={styles.volumeButton}>
                  {composerPlayControl.composerControls.volume > 0 ? (
                    <img src={VolumeIcon} alt="volume" />
                  ) : (
                    <MuteIcon className={styles.muteIcon} />
                  )}
                </div>
              </div>

              <div ref={speedOptionMenuRef} onMouseEnter={showFullScreenSpeedOpitonMenu} className={styles.fullScreenSpeedOptionWrapper}>
                {showFullScreenSpeedOption && (
                  <Box
                    className={qualityMenuStyles.playbackMenu}
                    sx={{
                      color: 'white',
                      marginBottom: '20px',
                      position: 'absolute',
                      bottom: '15px',
                      right: '-10px',
                    }}
                    onMouseLeave={closeFullScreenSpeedOptionMenu}
                  >
                    <div
                      onClick={selectSpeed}
                      id="0.25"
                      className={cx(
                        qualityMenuStyles.option,
                        composerPlayControl.composerControls.playbackRate === 0.25 && qualityMenuStyles.selectedOption,
                      )}
                    >
                      0.25
                    </div>
                    <div
                      onClick={selectSpeed}
                      id="0.5"
                      className={cx(
                        qualityMenuStyles.option,
                        composerPlayControl.composerControls.playbackRate === 0.5 && qualityMenuStyles.selectedOption,
                      )}
                    >
                      0.5
                    </div>
                    <div
                      onClick={selectSpeed}
                      id="0.75"
                      className={cx(
                        qualityMenuStyles.option,
                        composerPlayControl.composerControls.playbackRate === 0.75 && qualityMenuStyles.selectedOption,
                      )}
                    >
                      0.75
                    </div>
                    <div
                      onClick={selectSpeed}
                      id="1.0"
                      className={cx(
                        qualityMenuStyles.option,
                        composerPlayControl.composerControls.playbackRate === 1 && qualityMenuStyles.selectedOption,
                      )}
                    >
                      Normal
                    </div>
                    <div
                      onClick={selectSpeed}
                      id="1.25"
                      className={cx(
                        qualityMenuStyles.option,
                        composerPlayControl.composerControls.playbackRate === 1.25 && qualityMenuStyles.selectedOption,
                      )}
                    >
                      1.25
                    </div>
                    <div
                      onClick={selectSpeed}
                      id="1.5"
                      className={cx(
                        qualityMenuStyles.option,
                        composerPlayControl.composerControls.playbackRate === 1.5 && qualityMenuStyles.selectedOption,
                      )}
                    >
                      1.5
                    </div>
                    <div
                      onClick={selectSpeed}
                      id="2"
                      className={cx(
                        qualityMenuStyles.option,
                        composerPlayControl.composerControls.playbackRate === 2 && qualityMenuStyles.selectedOption,
                      )}
                    >
                      2
                    </div>
                  </Box>
                )}
                <div className={styles.playSpeedIcon}>
                  <PlaybackSpeed />
                </div>
              </div>

              <div className={styles.qualityWrapper}>
                <VideoQualitySelector videoData={videoData} />
              </div>

              <div onClick={switchFullScreen} className={styles.exitFullScreenIcon} onMouseEnter={closeFullScreenSpeedOptionMenu}>
                <img src={ExitFullScreen} alt="exit full screen" />
              </div>
            </div>
          </div>
          <div className={styles.fullScreenVideoRange}>
            <Slider
              min={0}
              max={videoWidth === videoWidth ? videoWidth : 100}
              value={progressValue}
              sx={{
                '& .MuiSlider-thumb': {
                  display: 'none',
                },
                '& .MuiSlider-rail': {
                  color: 'black',
                  height: '7px',
                },
                '& .MuiSlider-track': {
                  color: 'white',
                  height: '8px',
                },
              }}
              onChange={onPositionChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ComposerVideoControls;
